import { useEffect, useState } from "react";

import { useParams } from "react-router-dom";
import useAxios from "@hooks/use-axios";
import { PUBLISHER_ENDPOINTS } from "@constants/endpoints";
import { CONTRACT_STATUS } from "@panel/shared/contract/misc/constants";

const INITIAL = {
    loading        : true,
    kycPending     : false,
    contractPending: false,
    daysRemaining  : null,
    windowDays     : null
};

// Reads its own :publisherId off the route, so the banner needs nothing handed
// down from the page around it.
//
// Two calls, because no single endpoint carries all of it: the dashboard
// accounts list — the same one the topbar switcher reads — has this account's
// KYC and contract standing, and kyc/details has the window countdown. Each is
// caught on its own so a missing window still leaves the standing usable, and
// showAlert is off because an account that has not started KYC 404s on
// details, which is the very case the banner exists for.
const useKycBanner = () => {
    const { publisherId } = useParams();
    const { axiosInstance } = useAxios({ base: "affiliate", showAlert: false });
    const [state, setState] = useState(INITIAL);

    useEffect(() => {
        if (!publisherId) return;

        const fetchAccount = async () => {
            try {
                const res = await axiosInstance.get(PUBLISHER_ENDPOINTS.DASHBOARD_ACCOUNTS);
                const result = res.data || res;
                const items = Array.isArray(result) ? result : (result.items || result.results || []);

                return items.find((item) => item._id === publisherId) || null;
            } catch {
                return null;
            }
        };

        const fetchWindow = async () => {
            try {
                const res = await axiosInstance.get(PUBLISHER_ENDPOINTS.KYC_DETAILS(publisherId));
                const result = res.data || res;

                return result.data || result;
            } catch {
                return null;
            }
        };

        const load = async () => {
            const [account, kyc] = await Promise.all([fetchAccount(), fetchWindow()]);

            setState({
                loading        : false,
                kycPending     : Boolean(account) && account.kyc_status !== "verified",
                contractPending: Boolean(account) && account.contract_status === CONTRACT_STATUS.AWAITING,
                daysRemaining  : kyc?.days_remaining ?? null,
                windowDays     : kyc?.kyc_window_days ?? null
            });
        };

        load();
    }, [publisherId]); // eslint-disable-line react-hooks/exhaustive-deps

    return { publisherId, ...state };
};

export default useKycBanner;
